import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, DollarSign, ShoppingBag, Calendar, 
  LogOut, RefreshCw, Copy, Check, Tag, Percent, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InfluencerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [copied, setCopied] = useState(false);
  const tokenRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    // Get token from sessionStorage
    const storedToken = sessionStorage.getItem('_it');
    if (!storedToken) {
      navigate('/influencers');
      return;
    }
    tokenRef.current = storedToken;
    
    try {
      const res = await fetch(`${API_URL}/api/influencers/dashboard`, {
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`
        }
      });

      if (res.status === 401) {
        // Session expired or not authenticated
        sessionStorage.removeItem('_it');
        sessionStorage.removeItem('influencer_data');
        navigate('/influencers');
        return;
      }

      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      await fetch(`${API_URL}/api/influencers/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    sessionStorage.removeItem('_it');
    sessionStorage.removeItem('influencer_data');
    toast.success('Sesión cerrada');
    navigate('/influencers');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(dashboard?.influencer?.coupon_code || '');
    setCopied(true);
    toast.success('Código copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neon-darker flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neon-darker">
      {/* Header */}
      <header className="bg-neon-dark border-b border-neon-cyan/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-neon-cyan to-neon-pink rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">
                  Hola, {dashboard?.influencer?.name}
                </h1>
                <p className="text-xs text-white/50">Panel de Influencer</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-white/60 hover:text-neon-cyan transition-colors"
                title="Actualizar datos"
              >
                <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Coupon Code Card */}
        <div className="bg-gradient-to-r from-neon-cyan/20 to-neon-pink/20 border border-neon-cyan/30 rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                <Tag size={28} className="text-neon-cyan" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Tu código promocional</p>
                <p className="text-3xl font-bold text-white tracking-wider">
                  {dashboard?.influencer?.coupon_code}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-white/60 text-sm">Tu Comisión</p>
                {dashboard?.influencer?.commission_type === 'fixed' ? (
                  <p className="text-2xl font-bold text-neon-pink flex items-center gap-1">
                    <DollarSign size={20} />
                    {dashboard?.influencer?.commission_value}
                    <span className="text-sm font-normal text-white/50">/ perfume</span>
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-neon-pink flex items-center gap-1">
                    {dashboard?.influencer?.commission_value || dashboard?.influencer?.commission_percentage}
                    <Percent size={18} />
                  </p>
                )}
              </div>
              <button
                onClick={copyCode}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                title="Copiar código"
              >
                {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} className="text-white" />}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Orders */}
          <div className="bg-neon-dark border border-neon-cyan/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <ShoppingBag size={20} className="text-blue-400" />
              </div>
              <span className="text-white/60 text-sm">Pedidos Totales</span>
            </div>
            <p className="text-3xl font-bold text-white">{dashboard?.stats?.total_orders || 0}</p>
          </div>

          {/* Total Perfumes */}
          <div className="bg-neon-dark border border-neon-cyan/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Package size={20} className="text-purple-400" />
              </div>
              <span className="text-white/60 text-sm">Perfumes Vendidos</span>
            </div>
            <p className="text-3xl font-bold text-white">{dashboard?.stats?.total_perfumes || 0}</p>
          </div>

          {/* Total Commission */}
          <div className="bg-neon-dark border border-neon-cyan/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-neon-pink/20 rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-neon-pink" />
              </div>
              <span className="text-white/60 text-sm">Comisión Total</span>
            </div>
            <p className="text-3xl font-bold text-neon-pink">{formatCurrency(dashboard?.stats?.total_commission || 0)}</p>
          </div>

          {/* This Month */}
          <div className="bg-neon-dark border border-neon-cyan/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-neon-cyan/20 rounded-lg flex items-center justify-center">
                <Calendar size={20} className="text-neon-cyan" />
              </div>
              <span className="text-white/60 text-sm">Este Mes</span>
            </div>
            <p className="text-3xl font-bold text-neon-cyan">{formatCurrency(dashboard?.stats?.commission_this_month || 0)}</p>
            <p className="text-xs text-white/40 mt-1">
              {dashboard?.stats?.orders_this_month || 0} pedidos • {dashboard?.stats?.perfumes_this_month || 0} perfumes
            </p>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-neon-dark border border-neon-cyan/20 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neon-cyan/20">
            <h2 className="text-lg font-bold text-white">Últimos Pedidos</h2>
          </div>

          {dashboard?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neon-darker/50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs text-white/50 uppercase">Pedido</th>
                    <th className="text-left px-6 py-3 text-xs text-white/50 uppercase">Fecha</th>
                    <th className="text-center px-6 py-3 text-xs text-white/50 uppercase">Perfumes</th>
                    <th className="text-right px-6 py-3 text-xs text-white/50 uppercase">Venta</th>
                    <th className="text-right px-6 py-3 text-xs text-white/50 uppercase">Tu Comisión</th>
                    <th className="text-center px-6 py-3 text-xs text-white/50 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neon-cyan/10">
                  {dashboard.recent_orders.map((order) => (
                    <tr key={order.id} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-white font-mono text-sm">
                        #{order.order_number || order.id.slice(0, 8)}
                      </td>
                      <td className="px-6 py-4 text-white/60 text-sm">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4 text-white text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                          <Package size={12} />
                          {order.perfumes_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white text-right">
                        {formatCurrency(order.subtotal)}
                      </td>
                      <td className="px-6 py-4 text-neon-cyan font-bold text-right">
                        {formatCurrency(order.commission)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status === 'completed' ? 'Completado' :
                           order.status === 'pending' ? 'Pendiente' :
                           order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <ShoppingBag size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/40">Aún no hay pedidos con tu código</p>
              <p className="text-white/30 text-sm mt-1">Comparte tu código para comenzar a ganar comisiones</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InfluencerDashboard;
